import jwtDecode from 'jwt-decode';
import axios from 'axios';
import jwt_decode from 'jwt-decode';
import Cookies from 'js-cookie';
// The i18next SINGLETON, not `i18n/index.js`. That module is the same object
// (it configures this singleton and re-exports it) but it also carries
// `import.meta.glob`, and importing it here dragged that into every consumer of
// this file. Node's CJS loader cannot parse `import.meta`, so the whole
// Playwright suite died on load — `requests.js` is reachable from
// fixtures/auth.js via Utility → permissionsMatrix → matrix.
//
// Reading `.language` off the singleton is safe before init: it is undefined
// until `src/index.jsx` imports the config module, and the `|| 'en'` below
// already covers that window.
import i18n from 'i18next';
import queryClient from '../queryClient';
import { isStateChangingResponse, scheduleDerivedInvalidation } from './autoInvalidate';

const apiUrl = process.env.REACT_APP_API_URL ?? 'http://localhost:8080';

export const API_URL = apiUrl;

// Every request carries the APP locale, not the browser's. The reference-data
// endpoints (/country/*, /cngoods/*, /guiding-checklists, /release-notes) localize on
// Accept-Language; without this, a fetch would carry the OS/browser language
// and a German-browser user with the app in English would silently get German
// countries. Explicit `en` reliably forces English. On every other endpoint
// the header is ignored (language-neutral API) and it is a CORS-safelisted
// header, so sending it globally changes nothing else. A caller-provided
// Accept-Language wins.
axios.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  if (!config.headers['Accept-Language']) {
    config.headers['Accept-Language'] = i18n.language || 'en';
  }
  return config;
});

// Global axios response interceptor for trial expiration
axios.interceptors.response.use(
  (response) => {
    // A 2xx on a non-GET is the fact that makes Home's derived views untrue, so
    // this is where they get marked stale — every write in the app passes
    // through here, including the ~200 that never touch a mutation hook. See
    // the header of `autoInvalidate.js`.
    //
    // Wrapped: cache bookkeeping must never reject a response the user's save
    // depends on.
    try {
      if (isStateChangingResponse(response, apiUrl)) scheduleDerivedInvalidation(queryClient);
    } catch (err) {
      console.error('Derived-view invalidation failed', err);
    }
    return response;
  },
  (error) => {
    // Handle trial expiration 403 errors
    if (error.response?.status === 403) {
      const message = error.response?.data?.message || '';
      const errorField = error.response?.data?.error || '';
      const errorType = error.response?.data?.errorType || '';

      console.log('403 Error intercepted:', {
        url: error.config?.url,
        message,
        errorField,
        errorType,
        fullData: error.response?.data,
      });

      // Check if this is a trial expiration error
      // Machine code first (phase 6 D24); the prose heuristics below are the
      // older-backend fallback. TODO(retire barrier): delete the heuristics
      // one backend release after PR #312 deploys.
      const dataCode = error.response?.data?.code;
      const isTrialExpired =
        dataCode === 'TRIAL_EXPIRED' ||
        dataCode === 'NO_ACTIVE_PLAN' ||
        errorField.toLowerCase().includes('trial') ||
        errorField.toLowerCase().includes('expired') ||
        errorType.toLowerCase().includes('trial') ||
        errorType.toLowerCase().includes('expired') ||
        errorField === 'AuthorizationError';

      // A verifier firm has no plan to expire (verifier-firm-foundation VF18), so a 403 in its
      // workspace is never a trial problem — sending it to /trial-expired would strand it on a
      // pricing page it cannot use.
      const isVerifierOrg = window.Clerk?.organization?.publicMetadata?.type === 'verifier';

      if (isTrialExpired && !isVerifierOrg) {
        console.error(
          '🚫 Trial has expired, redirecting to trial expired page:',
          error.response.data,
        );

        // Redirect to trial expired page (avoid redirect loops)
        if (
          window.location.pathname !== '/trial-expired' &&
          window.location.pathname !== '/pricing' &&
          window.location.pathname !== '/onboarding/trial-status'
        ) {
          console.log('Redirecting to /trial-expired...');
          window.location.href = '/trial-expired';
        }
      }
    }

    return Promise.reject(error);
  },
);

// Drop the cached backend token. Called on sign-out so a revoked session's JWT
// can never linger in localStorage and get replayed on the next sign-in.
function clearClerkToken() {
  localStorage.removeItem('cg_backend_token');
  localStorage.removeItem('cg_backend_token_session');
}

// Returns a backend JWT scoped to the CURRENTLY ACTIVE organization.
//
// The cached token is validated against Clerk's authoritative active org —
// deliberately NOT against whatever a plain `getToken()` hands back. That
// distinction is load-bearing: for ~60s after an org switch Clerk's getToken()
// cache still serves the PREVIOUS org's token. The old implementation compared
// the cache against that stale token, saw the orgIds differ, and "corrected"
// the cache in the wrong direction — discarding the freshly minted new-org
// token (from refreshClerkToken) and re-adopting the stale one. Every request
// for the next ~60s then carried the OLD org's claims, so /permissions/me
// returned the previous org's permissions and the new org's overview was
// denied by PermissionRoute → /not-found.
//
// Any mismatch now re-mints with `skipCache`, so a stale-org token can never be
// adopted.
export async function getClerkToken() {
  const session = window.Clerk?.session;
  const cached = localStorage.getItem('cg_backend_token');
  // `session.lastActiveOrganizationId` is the authoritative "which org is
  // active" answer. `window.Clerk.organization` is DERIVED from it by looking
  // it up in the loaded membership list, so it resolves to null while that
  // list lags — exactly the window right after an org is activated (the
  // magic-link/onboarding boot does this). Reading the derived resource there
  // made an org-less cached token pass validation (null === null) and go out
  // on the first /permissions/me. Same reasoning as OrgBoundary's
  // hasActiveOrgId (src/modules/App/OrgBoundary.jsx).
  const activeOrgId = session?.lastActiveOrganizationId ?? window.Clerk?.organization?.id ?? null;

  if (cached) {
    try {
      const claims = jwtDecode(cached);
      const matchesActiveOrg = (claims.orgId ?? null) === activeOrgId;
      const unexpired = claims.exp > Date.now() / 1000;
      // `sid`/`sub` guard the sign-out → SSO re-login replay case: a token minted
      // under a now-dead session (same orgId) must not survive into the new one.
      // `sid` is the Clerk session id; `sub` is the user id.
      // The `prod` JWT template carries no `sid`, so the session a token was
      // minted under is recorded beside it; without that it never matched.
      const mintedFor = claims.sid ?? localStorage.getItem('cg_backend_token_session');
      const sameSession = mintedFor === session?.id && claims.sub === session?.user?.id;
      if (matchesActiveOrg && unexpired && sameSession) return cached;
    } catch (err) {
      // Undecodable cache — fall through and mint a fresh one.
    }
  }

  if (!session) return cached;
  return mintBackendToken(session, activeOrgId);
}

let pendingMint = null;

function mintBackendToken(session, activeOrgId, { force = false } = {}) {
  const key = `${session.id}:${activeOrgId}`;
  if (force || pendingMint?.key !== key) {
    const promise = session
      .getToken({ template: 'prod', skipCache: true })
      .then((token) => {
        localStorage.setItem('cg_backend_token', token);
        localStorage.setItem('cg_backend_token_session', session.id);
        return token;
      })
      .finally(() => {
        if (pendingMint?.promise === promise) pendingMint = null;
      });
    pendingMint = { key, promise };
  }
  return pendingMint.promise;
}

export function peekClerkToken() {
  const cached = localStorage.getItem('cg_backend_token');
  const session = window.Clerk?.session;
  if (!cached || !session) return null;
  try {
    const claims = jwtDecode(cached);
    const activeOrgId = session.lastActiveOrganizationId ?? window.Clerk?.organization?.id ?? null;
    const usable =
      (claims.orgId ?? null) === activeOrgId &&
      claims.exp > Date.now() / 1000 + 5 &&
      (claims.sid ?? localStorage.getItem('cg_backend_token_session')) === session.id &&
      claims.sub === session.user?.id;
    return usable ? cached : null;
  } catch {
    return null;
  }
}

async function refreshClerkToken() {
  const session = window.Clerk?.session;
  if (!session) return null;
  // `skipCache` is essential here: after an org switch Clerk keeps returning the
  // previous org's cached token for ~60s, so a normal getToken() would re-cache a
  // stale orgId. Forcing a fresh mint guarantees the new org's claims.
  return mintBackendToken(
    session,
    session.lastActiveOrganizationId ?? window.Clerk?.organization?.id ?? null,
    { force: true },
  );
}

// Whenever a request is sent to the backend the request's x-trace-id should match up to the aws-rum session id
// this way we can easily match up frontend RUM events to backend logs
let rumSessionId = null;

export function getSessionId() {
  if (rumSessionId === null) {
    let cookie = Cookies.get('cwr_s');
    if (cookie != null) {
      // the cookie is a JSON object that is base64 encoded
      let decodedCookie = JSON.parse(atob(cookie));
      rumSessionId = decodedCookie.sessionId;
    }
  }
  return rumSessionId;
}

export async function get(route, config = {}) {
  let token = await getClerkToken();
  let sessionId = getSessionId();
  const baseHeaders = {
    Authorization: 'Bearer ' + token,
    'x-trace-id': sessionId,
  };

  return axios.get(`${apiUrl}${route}`, {
    ...config,
    headers: {
      ...baseHeaders,
      ...(config.headers || {}),
    },
  });
}

export async function post(route, body, config = {}) {
  let token = await getClerkToken();
  let sessionId = getSessionId();
  return axios.post(`${apiUrl}${route}`, body, {
    ...config,
    headers: {
      Authorization: 'Bearer ' + token,
      'x-trace-id': sessionId,
      ...(config.headers || {}),
    },
  });
}

export async function patch(route, body) {
  let token = await getClerkToken();
  let sessionId = getSessionId();
  return axios.patch(`${apiUrl}${route}`, body, {
    headers: {
      Authorization: 'Bearer ' + token,
      'x-trace-id': sessionId,
    },
  });
}

export async function put(route, body) {
  let token = await getClerkToken();
  let sessionId = getSessionId();
  return axios.put(`${apiUrl}${route}`, body, {
    headers: {
      Authorization: 'Bearer ' + token,
      'x-trace-id': sessionId,
    },
  });
}

export async function publicPost(route, body) {
  let sessionId = getSessionId();
  return axios.post(`${apiUrl}${route}`, body, {
    headers: {
      'x-trace-id': sessionId,
    },
  });
}

export async function publicGet(route) {
  let sessionId = getSessionId();
  return axios.get(`${apiUrl}${route}`, {
    headers: {
      'x-trace-id': sessionId,
    },
  });
}

export async function remove(route, body) {
  let token = await getClerkToken();
  let sessionId = getSessionId();
  return axios.delete(`${apiUrl}${route}`, {
    data: body,
    headers: {
      Authorization: 'Bearer ' + token,
      'x-trace-id': sessionId,
    },
  });
}

export async function GetUserId() {
  var id;
  try {
    var token = localStorage.getItem('clerk-db-jwt');
    var decoded = jwt_decode(token);
    id = decoded.id;
  } catch (e) {
    console.log(e);
  }
  var email = await axios.get(`${apiUrl}/declerant`, {
    headers: {
      Authorization: 'Bearer ' + token,
      'user-id': decoded.id,
    },
  })?.email;

  return email;
}

export function getUserOrgs() {
  var orgs;
  try {
    var token = localStorage.getItem('cg_backend_token');
    var decoded = jwt_decode(token);
    orgs = decoded.orgs;
  } catch (e) {
    console.err(e);
  }
  return orgs;
}

const http = {
  get,
  post,
  patch,
  put,
  remove,
  publicPost,
  publicGet,
  refreshClerkToken,
  clearClerkToken,
  getUserOrgs,
};
export default http;
